import { Agent } from '@atproto/api'
import type { OAuthSession } from '@atproto/oauth-client-browser'
import { buildPlcDidDocUrl } from '@mackinac/shared'
import { CONSTELLATION_BASE_URL } from '@/constants/verification'
import { AT_EXPLORE_BASE_URL } from '@/constants/api'
import type {
    ConstellationLink,
    IssuedVerificationRecord,
    VerificationRecord,
    TrustedVerifier,
} from '@/types/verification'

interface VerifierApiEntry {
    handle: string
    did: string
}

/**
 * Fetch the canonical list of default verifiers from the API.
 */
export async function fetchDefaultVerifiers(): Promise<TrustedVerifier[]> {
    const response = await fetch(`${AT_EXPLORE_BASE_URL}/api/verifiers?limit=100`)
    if (!response.ok) throw new Error(`Failed to fetch verifiers: ${response.status}`)
    const json: unknown = await response.json()
    const data = (json as { result?: unknown }).result ?? json
    if (!Array.isArray(data)) throw new Error('Unexpected verifiers response shape')
    return data
        .filter(
            (entry): entry is VerifierApiEntry =>
                typeof entry === 'object' &&
                entry !== null &&
                typeof (entry as VerifierApiEntry).did === 'string' &&
                typeof (entry as VerifierApiEntry).handle === 'string'
        )
        .map(entry => ({ did: entry.did, label: entry.handle, handle: entry.handle }))
}

interface ConstellationResponse {
    linking_records: ConstellationLink[]
}

interface VerificationRecordValue {
    $type: string
    subject: string
    handle: string
    displayName: string
    createdAt: string
}

interface PdsGetRecordResponse {
    uri: string
    cid: string
    value: VerificationRecordValue
}

interface PlcDocument {
    service: { id: string; type: string; serviceEndpoint: string }[]
}

/**
 * Query Constellation's backlink index for verification records targeting a DID
 * from specific trusted verifier DIDs.
 */
export async function fetchConstellationVerifications(
    targetDid: string,
    trustedDids: string[]
): Promise<ConstellationLink[]> {
    if (trustedDids.length === 0) return []

    const params = new URLSearchParams({
        target: targetDid,
        collection: 'app.bsky.graph.verification',
        path: '.subject',
        from_dids: trustedDids.join(','),
    })

    const response = await fetch(`${CONSTELLATION_BASE_URL}/links?${params}`)
    if (!response.ok) return []

    const data: ConstellationResponse = await response.json()
    return data.linking_records ?? []
}

/**
 * Resolve a DID to its PDS service endpoint via PLC directory.
 */
async function resolvePdsUrl(did: string): Promise<string | null> {
    try {
        const response = await fetch(buildPlcDidDocUrl(did), { signal: AbortSignal.timeout(5000) })
        if (!response.ok) return null
        const doc: PlcDocument = await response.json()
        const pdsService = doc.service?.find(
            s => s.id === '#atproto_pds' || s.type === 'AtprotoPersonalDataServer'
        )
        const endpoint = pdsService?.serviceEndpoint
        if (!endpoint) return null
        // Validate that the resolved URL is a safe https endpoint
        const parsed = new URL(endpoint)
        if (parsed.protocol !== 'https:') return null
        return endpoint
    } catch {
        return null
    }
}

/**
 * Fetch an actual verification record from a verifier's PDS.
 */
export async function fetchVerificationRecord(
    verifierDid: string,
    rkey: string
): Promise<VerificationRecordValue | null> {
    const pdsUrl = await resolvePdsUrl(verifierDid)
    if (!pdsUrl) return null

    const params = new URLSearchParams({
        repo: verifierDid,
        collection: 'app.bsky.graph.verification',
        rkey,
    })

    const response = await fetch(`${pdsUrl}/xrpc/com.atproto.repo.getRecord?${params}`)
    if (!response.ok) return null

    const data: PdsGetRecordResponse = await response.json()
    return data.value ?? null
}

/**
 * Resolve custom verification for a target DID against a set of trusted verifiers.
 * Returns verified records with validation status.
 */
export async function resolveCustomVerification(
    targetDid: string,
    currentHandle: string,
    currentDisplayName: string,
    trustedVerifiers: TrustedVerifier[]
): Promise<VerificationRecord[]> {
    const trustedDids = trustedVerifiers.map(v => v.did)
    const links = await fetchConstellationVerifications(targetDid, trustedDids)
    if (links.length === 0) return []

    const verifierLabelMap = new Map(trustedVerifiers.map(v => [v.did, v.label]))

    const results = await Promise.allSettled(
        links.map(async (link): Promise<VerificationRecord> => {
            const record = await fetchVerificationRecord(link.did, link.rkey)

            if (!record) {
                return {
                    verifierDid: link.did,
                    verifierLabel: verifierLabelMap.get(link.did) ?? link.did,
                    subject: '',
                    handle: '',
                    displayName: '',
                    createdAt: '',
                    isValid: false,
                }
            }

            const isValid =
                record.subject === targetDid &&
                record.handle === currentHandle &&
                record.displayName === currentDisplayName

            return {
                verifierDid: link.did,
                verifierLabel: verifierLabelMap.get(link.did) ?? link.did,
                subject: record.subject,
                handle: record.handle,
                displayName: record.displayName,
                createdAt: record.createdAt,
                isValid,
            }
        })
    )

    return results
        .filter((r): r is PromiseFulfilledResult<VerificationRecord> => r.status === 'fulfilled')
        .map(r => r.value)
}

/**
 * Create a verification record on the current user's PDS.
 */
export async function createVerificationRecord(
    session: OAuthSession,
    targetDid: string,
    targetHandle: string,
    targetDisplayName: string
): Promise<{ uri: string; rkey: string }> {
    const agent = new Agent(session)
    const response = await agent.com.atproto.repo.createRecord({
        repo: session.did,
        collection: 'app.bsky.graph.verification',
        record: {
            $type: 'app.bsky.graph.verification',
            subject: targetDid,
            handle: targetHandle,
            displayName: targetDisplayName,
            createdAt: new Date().toISOString(),
        },
    })
    const uri = response.data.uri
    const rkey = uri.split('/').pop()
    if (!rkey) {
        throw new Error(`createVerificationRecord: unexpected URI format "${uri}"`)
    }
    return { uri, rkey }
}

/**
 * Delete a verification record from the current user's PDS.
 */
export async function deleteVerificationRecord(session: OAuthSession, rkey: string): Promise<void> {
    const agent = new Agent(session)
    await agent.com.atproto.repo.deleteRecord({
        repo: session.did,
        collection: 'app.bsky.graph.verification',
        rkey,
    })
}

interface ListRecordsPage {
    records: Array<{
        uri: string
        cid: string
        value: VerificationRecordValue
    }>
    cursor?: string
}

function mapRecord(record: {
    uri: string
    value: VerificationRecordValue
}): IssuedVerificationRecord {
    const value = record.value
    const rkey = record.uri.split('/').pop() ?? ''
    return {
        uri: record.uri,
        rkey,
        subject: value?.subject ?? '',
        handle: value?.handle ?? '',
        displayName: value?.displayName ?? '',
        createdAt: value?.createdAt ?? '',
    }
}

/**
 * List one page of verification records issued by the current user from their PDS.
 * Returns the records and an optional cursor for the next page.
 */
export async function listIssuedVerifications(
    session: OAuthSession,
    cursor?: string
): Promise<{ records: IssuedVerificationRecord[]; cursor?: string }> {
    const agent = new Agent(session)

    const response = await agent.com.atproto.repo.listRecords({
        repo: session.did,
        collection: 'app.bsky.graph.verification',
        limit: 100,
        cursor,
    })

    const page = response.data as unknown as ListRecordsPage
    const records = Array.isArray(page.records) ? page.records.map(mapRecord) : []
    return { records, cursor: page.cursor }
}
